// Mirror of backend App\Support\PassageText — book scans reach the student with
// running page numbers stranded mid-sentence, an orphaned dropped capital and the
// chapter/title block run into the first paragraph.

const WIN1252 = {
  "\u0080": "\u20AC", "\u0082": "\u201A", "\u0083": "\u0192", "\u0084": "\u201E",
  "\u0085": "\u2026", "\u0086": "\u2020", "\u0087": "\u2021", "\u0088": "\u02C6",
  "\u0089": "\u2030", "\u008A": "\u0160", "\u008B": "\u2039", "\u008C": "\u0152",
  "\u008E": "\u017D", "\u0091": "\u2018", "\u0092": "\u2019", "\u0093": "\u201C",
  "\u0094": "\u201D", "\u0095": "\u2022", "\u0096": "\u2013", "\u0097": "\u2014",
  "\u0098": "\u02DC", "\u0099": "\u2122", "\u009A": "\u0161", "\u009B": "\u203A",
  "\u009C": "\u0153", "\u009E": "\u017E", "\u009F": "\u0178",
};

const CHAPTER_WORDS = new Set([
  "ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN",
  "EIGHTEEN", "NINETEEN", "TWENTY", "PROLOGUE", "EPILOGUE",
]);

const isPageNumberLine = (line) =>
  line !== "" && /^[[(\-–—|\s]*(?:page\s*)?\d{1,4}[\])\-–—|\s]*$/i.test(line);

export const isChapterLabel = (line) => {
  const normalized = (line || "").trim().toUpperCase();
  if (/^(CHAPTER|PART|BOOK|SECTION)\b[\s\-–—]*[IVXLCDM0-9]*\.?$/.test(normalized)) return true;
  if (/^[IVXLCDM]{1,7}\.?$/.test(normalized)) return true;
  if (/^\d{1,3}\.?$/.test(normalized)) return true;
  return CHAPTER_WORDS.has(normalized);
};

const looksLikeHeadingLine = (line, allowLowercase) => {
  if (line.length > 70 || line.split(/\s+/).filter(Boolean).length > 12) return false;
  if (/[.!?;:,"”’]$/u.test(line)) return false;
  if (/^[“"‘'(]/u.test(line)) return false;
  return isChapterLabel(line) || allowLowercase || /^[A-Z0-9]/u.test(line);
};

const reattachDropCap = (lines, letter) => {
  for (let i = 0; i < lines.length; i += 1) {
    // A short all-caps line is a heading, not the tail of a broken word.
    if (lines[i].length < 40 || !/[a-z]/.test(lines[i])) continue;
    if (/^[A-Z]{2,}\b/.test(lines[i])) {
      lines[i] = letter + lines[i];
      return lines;
    }
  }
  return lines;
};

export const cleanPassageText = (value) => {
  if (typeof value !== "string" || value === "") return "";

  let text = value.replace(/[\u0080-\u009F]/g, (c) => WIN1252[c] || "");
  text = text
    .replace(/\r\n?/g, "\n")
    .replace(/\u00A0/g, " ")
    .replace(/[\uFEFF\uFFFD]/g, "");

  const lines = text.split("\n").map((line) => line.replace(/[ \t]+/g, " ").trim());

  const kept = [];
  const orphanLetters = [];
  let skipping = false;
  lines.forEach((line) => {
    const dropCap = /^([A-Z])$/.exec(line);
    if (isPageNumberLine(line) || dropCap) {
      if (dropCap) orphanLetters.push(dropCap[1]);
      while (kept.length && kept[kept.length - 1] === "") kept.pop();
      skipping = kept.length > 0;
      return;
    }
    if (skipping) {
      if (line === "") return;
      skipping = false;
    }
    kept.push(line);
  });

  let result = kept;
  orphanLetters.forEach((letter) => {
    result = reattachDropCap(result, letter);
  });

  const out = [];
  let blankRun = 0;
  result.forEach((line) => {
    if (line === "") {
      blankRun += 1;
      if (blankRun > 1) return;
    } else {
      blankRun = 0;
    }
    out.push(line);
  });

  return out.join("\n").trim();
};

// Book scans are hard-wrapped at the scanner's column width, one physical line per \n,
// with no blank line between paragraphs — so a paragraph boundary can't be told apart
// from a mid-sentence wrap by looking at whitespace alone. Primary signal: a line that
// doesn't end its sentence continues on the next line. Also continue when a double
// quote is still open (wrap after "No, Meg.") or the next line starts lower-case.
const SENTENCE_END_RE = /[.!?…][)\]"'’”]*$/;

const hasOpenDoubleQuote = (s) => {
  let open = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === "\u201C") open = true;
    else if (ch === "\u201D") open = false;
    else if (ch === '"') open = !open;
  }
  return open;
};

const shouldMergeWithNext = (prev, next) => {
  if (!prev || prev === "") return false;
  if (!SENTENCE_END_RE.test(prev)) return true;
  if (hasOpenDoubleQuote(prev)) return true;
  if (/^[a-z]/.test(next)) return true;
  return false;
};

export const mergeWrappedLines = (text) => {
  if (typeof text !== "string" || text === "") return text;
  const lines = text.split("\n");
  const merged = [];
  lines.forEach((line) => {
    if (line === "") {
      merged.push(line);
      return;
    }
    const prev = merged.length ? merged[merged.length - 1] : null;
    if (shouldMergeWithNext(prev, line)) {
      merged[merged.length - 1] = `${prev} ${line}`;
    } else {
      merged.push(line);
    }
  });
  return merged.join("\n");
};

// True when sub-questions cite "line N" / "lines N–M" — exam papers need hard-wrap
// fidelity; book-scan worksheets do not.
export const passageQuestionsCiteLines = (questions = []) =>
  questions.some((q) => /\blines?\s+\d/i.test(q?.question_text || q?.question || q?.name || ""));

// → { chapter, title, body }
export const structurePassage = (value) => {
  const body = cleanPassageText(value);
  if (!body) return { chapter: null, title: null, body: "" };

  const lines = body.split("\n");
  const headingLines = [];
  let index = 0;
  while (index < lines.length && headingLines.length < 4) {
    const line = lines[index];
    if (line === "") {
      if (headingLines.length === 0) {
        index += 1;
        continue;
      }
      break;
    }
    // A chapter label licenses the wrapped subtitle beneath it, which often
    // continues in lower case ("…takes place / which seems likely to…").
    const allowLowercase = headingLines.length > 0 && isChapterLabel(headingLines[0]);
    if (!looksLikeHeadingLine(line, allowLowercase)) break;
    headingLines.push(line);
    index += 1;
  }

  const rest = lines.slice(index).join("\n").trim();
  if (headingLines.length === 0 || rest === "") {
    return { chapter: null, title: null, body: mergeWrappedLines(body) };
  }

  const chapter = isChapterLabel(headingLines[0]) ? headingLines.shift() : null;
  const title = headingLines.length ? headingLines.join(" ") : null;
  if (!chapter && !title) return { chapter: null, title: null, body: mergeWrappedLines(body) };

  return { chapter, title, body: mergeWrappedLines(rest) };
};
